import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePickerPopover } from './ui/date-picker-popover';
import { TimePickerPopover } from './ui/time-picker-popover';

// ScheduleDateTimeDialog is a generic "pick a future date + time and
// confirm" modal. Used by the website scheduled-publish flow today;
// design is intentionally generic so other scheduled-action surfaces
// (e.g. scheduled broadcasts, scheduled invoice issues) can reuse it.
//
// Date and time are kept as separate ISO `YYYY-MM-DD` / `HH:MM`
// strings per CLAUDE.md (no `<input type="date">` / `time` — they
// follow browser locale and break for en-US users). We combine them
// into a single Date on submit using the user's local timezone, then
// serialise to RFC 3339 UTC for the API.
//
// The fields are click-to-pick (calendar grid + hour/minute columns)
// rather than typing fields: "schedule this for later" is a browsing
// decision — which weekday is the 14th? — not a transcription one.
// The value contract is unchanged, so callers still get RFC 3339 UTC.
//
// The calendar popover portals into the <dialog> element itself, NOT
// into document.body and NOT into `__inner`. showModal() promotes the
// dialog to the top layer, so a body-portaled panel renders behind it
// regardless of z-index; `__inner` is an overflow-y:auto scroll box
// that would clip the panel instead.

export interface ScheduleDateTimeDialogProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel: string;
  // initialDate / initialTime optional — defaults to "today + 1h"
  // (rounded to the nearest 15 minutes) so the first state is always
  // a valid future timestamp.
  initialDate?: string;
  initialTime?: string;
  onConfirm: (whenISO: string) => Promise<void> | void;
  onClose: () => void;
}

export function ScheduleDateTimeDialog({
  open,
  title,
  body,
  confirmLabel,
  initialDate,
  initialTime,
  onConfirm,
  onClose,
}: ScheduleDateTimeDialogProps) {
  const { t, i18n } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  // Held in state, not a ref: the popover needs the node on the render
  // that mounts it, and a ref assignment doesn't re-render.
  const [portalHost, setPortalHost] = useState<HTMLDialogElement | null>(null);
  const defaults = defaultDateTime();
  const [date, setDate] = useState(initialDate ?? defaults.date);
  const [time, setTime] = useState(initialTime ?? defaults.time);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  // Reset state every time the dialog reopens so an admin who closed
  // it half-edited yesterday doesn't see stale values today.
  useEffect(() => {
    if (open) {
      const d = defaultDateTime();
      setDate(initialDate ?? d.date);
      setTime(initialTime ?? d.time);
      setErr(null);
    }
  }, [open, initialDate, initialTime]);

  const submit = async () => {
    setErr(null);
    if (!isValidISODate(date) || !isValidHHMM(time)) {
      setErr(t('scheduleDialog.invalid'));
      return;
    }
    const local = new Date(`${date}T${time}:00`);
    if (Number.isNaN(local.getTime())) {
      setErr(t('scheduleDialog.invalid'));
      return;
    }
    if (local.getTime() < Date.now() + 60_000) {
      setErr(t('scheduleDialog.tooSoon'));
      return;
    }
    setBusy(true);
    try {
      await onConfirm(local.toISOString());
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={(node) => {
        dialogRef.current = node;
        setPortalHost(node);
      }}
      className="schedule-dialog"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="schedule-dialog__inner" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {body && <p>{body}</p>}
        <div className="schedule-dialog__fields">
          <div className="schedule-dialog__field">
            <span className="schedule-dialog__field-label">{t('scheduleDialog.date')}</span>
            <DatePickerPopover
              value={date}
              onChange={setDate}
              locale={i18n.language}
              min={todayISO()}
              placeholder={t('scheduleDialog.datePlaceholder')}
              container={portalHost}
            />
          </div>
          <div className="schedule-dialog__field">
            <span className="schedule-dialog__field-label">{t('scheduleDialog.time')}</span>
            <TimePickerPopover
              value={time}
              onChange={setTime}
              minuteStep={5}
              ariaLabel={t('scheduleDialog.time')}
            />
          </div>
        </div>
        {err && <p className="schedule-dialog__error" role="alert">{err}</p>}
        <footer className="schedule-dialog__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => void submit()}
          >
            {busy ? t('common.submitting') : confirmLabel}
          </button>
        </footer>
      </div>
    </dialog>
  );
}

// defaultDateTime picks "an hour from now, rounded up to the next
// 15-minute slot" in local time. Lands on a useful first-keystroke
// value for the common "publish tomorrow morning" flow without
// pre-filling something obviously stale.
function defaultDateTime(): { date: string; time: string } {
  const t = new Date();
  t.setMinutes(t.getMinutes() + 60);
  const m = t.getMinutes();
  t.setMinutes(m + (15 - (m % 15)) % 15);
  t.setSeconds(0);
  t.setMilliseconds(0);
  const yyyy = t.getFullYear();
  const mm = String(t.getMonth() + 1).padStart(2, '0');
  const dd = String(t.getDate()).padStart(2, '0');
  const hh = String(t.getHours()).padStart(2, '0');
  const mi = String(t.getMinutes()).padStart(2, '0');
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

// todayISO is the calendar floor for the date picker — you can't schedule
// something into the past. The exact "at least a minute from now" check
// still runs on submit; this only greys out whole days that are already over.
function todayISO(): string {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function isValidISODate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function isValidHHMM(s: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(s)) return false;
  const [h, m] = s.split(':').map((n) => parseInt(n, 10));
  return h !== undefined && m !== undefined && h >= 0 && h < 24 && m >= 0 && m < 60;
}
